import axios, { AxiosInstance, AxiosError } from 'axios';

// Create axios instance with default config
// Priority: 1) Environment variable, 2) Production URL, 3) Local development
const API_BASE_URL = process.env.REACT_APP_API_URL ||
  (process.env.NODE_ENV === 'production'
    ? 'https://gravely-reporting-backend.onrender.com'
    : 'http://localhost:5000');

// Log API URL for debugging (only in development)
if (process.env.NODE_ENV !== 'production') {
  console.log('API Base URL:', API_BASE_URL);
}

// Phase 1.3: Request deduplication cache
// Prevents duplicate simultaneous requests to the same endpoint
interface PendingRequest {
  promise: Promise<any>;
  timestamp: number;
}

const pendingRequests = new Map<string, PendingRequest>();
const REQUEST_CACHE_TTL = 300000; // 5 minutes

/**
 * Generate cache key from request config
 */
function getCacheKey(method: string, url: string, params?: any): string {
  const paramStr = params ? JSON.stringify(params) : '';
  return `${method.toUpperCase()}:${url}:${paramStr}`;
}

/**
 * Clean expired cache entries
 */
function cleanExpiredCache(): void {
  const now = Date.now();
  // Use Array.from to avoid TypeScript downlevelIteration issue
  const entries = Array.from(pendingRequests.entries());
  for (const [key, request] of entries) {
    if (now - request.timestamp > REQUEST_CACHE_TTL) {
      pendingRequests.delete(key);
    }
  }
}

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Phase 1.3: Wrap api.get to track pending requests
const originalGet = api.get.bind(api);
api.get = function(url: string, config?: any): Promise<any> {
  const cacheKey = getCacheKey('GET', url, config?.params);

  // Check if request is already pending
  const pending = pendingRequests.get(cacheKey);
  if (pending) {
    console.log(`🔄 DEDUP: Reusing pending GET request for ${url}`);
    return pending.promise;
  }

  // Create new request promise
  const promise = originalGet(url, config);

  // Track this request
  pendingRequests.set(cacheKey, {
    promise,
    timestamp: Date.now()
  });

  // Clean up after completion (both success and error)
  promise
    .then((response) => {
      pendingRequests.delete(cacheKey);
      return response;
    })
    .catch((error) => {
      pendingRequests.delete(cacheKey);
      throw error;
    });

  return promise;
};

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Phase 1.3: Clean expired cache periodically
    if (Math.random() < 0.1) { // 10% chance on each request
      cleanExpiredCache();
    }

    // Add JWT token if available (for cookie-restricted environments)
    const token = localStorage.getItem('auth_token');
    console.log('Axios interceptor - token from localStorage:', token ? 'present' : 'missing');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('Axios interceptor - added Authorization header');
    }
    console.log('Axios request headers:', config.headers);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error: AxiosError) => {
    if (error.response?.status === 401) {
      // Don't redirect if we're already on the login page or making a login request
      const isLoginPage = window.location.pathname === '/login';
      const isLoginRequest = error.config?.url?.includes('/auth/login') ||
                            error.config?.url?.includes('/auth/v2/login');

      if (!isLoginPage && !isLoginRequest) {
        // Handle unauthorized access
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  login: async (email: string, password: string) => {
    // Manager login (corporate, regional, district) - uses email
    console.log('Making manager login API call...');
    const response = await api.post('/api/auth/v2/login', { email, password });
    console.log('Manager login API response:', response.data);
    console.log('Token in manager login response:', response.data.token ? 'present' : 'missing');
    return response.data;
  },

  dealerLogin: async (dealerId: string, password: string) => {
    // Dealer login - uses dealer ID instead of email
    console.log('Making dealer login API call...');
    const response = await api.post('/api/auth/v2/dealer/login', { dealer_id: dealerId, password });
    console.log('Dealer login API response:', response.data);
    console.log('Token in dealer login response:', response.data.token ? 'present' : 'missing');
    return response.data;
  },

  logout: async () => {
    // Use v2 auth logout endpoint to match login
    const response = await api.post('/api/auth/v2/logout');
    return response.data;
  },

  getCurrentUser: async () => {
    // Use v2 auth user endpoint to match login system
    const response = await api.get('/api/auth/v2/user');
    return response.data;
  },

  checkAuth: async () => {
    // After migration to 100% database system, ALL users use v2 auth
    const response = await api.get('/api/auth/v2/check');
    return response.data;
  },
};

// Reports API - V2 (Real Data)
export const reportsAPI = {
  // CONSOLIDATED ENDPOINT - Fetches all dashboard data in one request (Optimization #2)
  getConsolidatedDashboard: async (dealerId?: string, startDate?: string, endDate?: string, radius?: number, districtManager?: string, regionalManager?: string) => {
    const params = new URLSearchParams();
    if (dealerId) params.append('dealer_id', dealerId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (radius) params.append('radius', radius.toString());
    if (districtManager) params.append('district_manager', districtManager);
    if (regionalManager) params.append('regional_manager', regionalManager);

    const response = await api.get(`/api/v2/reports/dashboard/consolidated?${params.toString()}`);
    return response.data;
  },

  // Lazy-loaded corporate map data (only fetched when user opens Map View tab)
  getCorporateMapData: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await api.get(`/api/v2/reports/dashboard/corporate/map?${params.toString()}`);
    return response.data;
  },

  // NEW V2 API - Uses real imported data from advertising_data_* tables
  getDashboard: async (dealerId?: string, startDate?: string, endDate?: string, radius?: number, districtManager?: string) => {
    const params = new URLSearchParams();
    if (dealerId) params.append('dealer_id', dealerId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (radius) params.append('radius', radius.toString());
    if (districtManager) params.append('district_manager', districtManager);

    const response = await api.get(`/api/v2/reports/dashboard?${params.toString()}`);
    return response.data;
  },

  getSummary: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/api/v2/reports/summary?${params.toString()}`);
    return response.data;
  },

  getChartData: async (channel: string, dealerId?: string, startDate?: string, endDate?: string, radius?: number) => {
    const params = new URLSearchParams();
    if (dealerId) params.append('dealer_id', dealerId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (radius) params.append('radius', radius.toString());

    const response = await api.get(`/api/v2/reports/charts/${channel}?${params.toString()}`);
    return response.data;
  },

  getMapData: async (dealerId?: string, startDate?: string, endDate?: string, radius?: number) => {
    const params = new URLSearchParams();
    if (dealerId) params.append('dealer_id', dealerId);
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (radius) params.append('radius', radius.toString());

    const response = await api.get(`/api/v2/reports/map?${params.toString()}`);
    return response.data;
  },

  getSpots: async (startDate?: string, endDate?: string, type?: string, dealerId?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (type) params.append('type', type);
    if (dealerId) params.append('dealer_id', dealerId);  // For managers viewing dealer data

    const response = await api.get(`/api/v2/reports/spots?${params.toString()}`);
    return response.data;
  },

  getDistrictSpots: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/api/v2/reports/spots/district?${params.toString()}`);
    return response.data;
  },

  getRegionalSpots: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/api/v2/reports/spots/region?${params.toString()}`);
    return response.data;
  },

  getCorporateSpots: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/api/v2/reports/spots/corporate?${params.toString()}`);
    return response.data;
  },

  getMlbSocialSummary: async (startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    const response = await api.get(`/api/v2/reports/mlb-social-summary?${params.toString()}`);
    return response.data;
  },

  getDealers: async () => {
    const response = await api.get('/api/v2/reports/dealers');
    return response.data;
  },

  // Legacy API (Demo Data) - Keep for backwards compatibility
  getRadioData: async (distributorId?: number, startDate?: string, endDate?: string) => {
    const params = new URLSearchParams();
    if (distributorId) params.append('distributor_id', distributorId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);

    const response = await api.get(`/api/reports/radio?${params.toString()}`);
    return response.data;
  },

  getAllReportData: async (distributorId?: number, startDate?: string, endDate?: string, radius?: number) => {
    const params = new URLSearchParams();
    if (distributorId) params.append('distributor_id', distributorId.toString());
    if (startDate) params.append('start_date', startDate);
    if (endDate) params.append('end_date', endDate);
    if (radius) params.append('radius', radius.toString());

    const response = await api.get(`/api/reports/all?${params.toString()}`);
    return response.data;
  },
};

// Data API
export const dataAPI = {
  getDistributors: async () => {
    const response = await api.get('/api/data/distributors');
    return response.data;
  },
  
  getDistributor: async (distributorId: number) => {
    const response = await api.get(`/api/data/distributors/${distributorId}`);
    return response.data;
  },
  
  getDistributorZips: async (distributorId: number) => {
    const response = await api.get(`/api/data/distributors/${distributorId}/zips`);
    return response.data;
  },
  
  getRegions: async () => {
    const response = await api.get('/api/data/regions');
    return response.data;
  },
  
  getRegionDistricts: async (regionId: string) => {
    const response = await api.get(`/api/data/regions/${regionId}/districts`);
    return response.data;
  },
  
  getDistrictDealers: async (districtId: string) => {
    const response = await api.get(`/api/data/districts/${districtId}/dealers`);
    return response.data;
  },
  
  getDealersByHierarchy: async (regionId?: string, districtId?: string) => {
    const params = new URLSearchParams();
    if (regionId) params.append('region_id', regionId);
    if (districtId) params.append('district_id', districtId);
    
    const response = await api.get(`/api/data/dealers/hierarchy?${params.toString()}`);
    return response.data;
  },
};

// Admin API endpoints
export const adminAPI = {
  getSystemStats: async () => {
    const response = await api.get('/api/admin/stats');
    return response.data;
  },

  healthCheck: async () => {
    const response = await api.get('/api/admin/health');
    return response.data;
  },

  getVisibilityMetrics: async () => {
    const response = await api.get('/api/admin/visibility-metrics');
    return response.data;
  },

  recalculateVisibility: async (days: number = 30) => {
    const response = await api.post('/api/admin/recalculate-visibility', { days });
    return response.data;
  },

  // User Management
  getAllUsers: async (params?: {
    page?: number;
    per_page?: number;
    role?: string;
    is_active?: boolean;
    search?: string;
    reports_to?: string;  // Phase 2.2.5: Filter district managers by regional manager
    inactive_days?: number;  // Phase 2.2.5: Filter by inactivity (30/60/90 days)
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);
    if (params?.reports_to) queryParams.append('reports_to', params.reports_to);
    if (params?.inactive_days) queryParams.append('inactive_days', params.inactive_days.toString());

    const response = await api.get(`/api/admin/users/all?${queryParams.toString()}`);
    return response.data;
  },

  // Phase 2.2.5: Get regional managers list
  getRegionalManagers: async () => {
    const response = await api.get('/api/admin/users/regional-managers');
    return response.data;
  },

  // Dealer Management
  createDealer: async (dealerData: any) => {
    const response = await api.post('/api/admin/users/dealers', dealerData);
    return response.data;
  },

  updateDealer: async (dealerId: string, dealerData: any) => {
    const response = await api.put(`/api/admin/users/dealers/${dealerId}`, dealerData);
    return response.data;
  },

  deleteDealer: async (dealerId: string) => {
    const response = await api.delete(`/api/admin/users/dealers/${dealerId}`);
    return response.data;
  },

  getDealer: async (dealerId: string) => {
    const response = await api.get(`/api/admin/users/dealers/${dealerId}`);
    return response.data;
  },

  listDealers: async (params?: { page?: number; per_page?: number; is_active?: boolean; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get(`/api/admin/users/dealers?${queryParams.toString()}`);
    return response.data;
  },

  // Manager Management
  createManager: async (managerData: any) => {
    const response = await api.post('/api/admin/users/managers', managerData);
    return response.data;
  },

  getManager: async (userId: number) => {
    const response = await api.get(`/api/admin/users/managers/${userId}`);
    return response.data;
  },

  updateManager: async (userId: number, managerData: any) => {
    const response = await api.put(`/api/admin/users/managers/${userId}`, managerData);
    return response.data;
  },

  deleteManager: async (userId: number) => {
    const response = await api.delete(`/api/admin/users/managers/${userId}`);
    return response.data;
  },

  reassignDealers: async (userId: number, dealerIds: string[], action: 'add' | 'remove') => {
    const response = await api.post(`/api/admin/users/managers/${userId}/reassign-dealers`, {
      dealer_ids: dealerIds,
      action,
    });
    return response.data;
  },

  reassignDistricts: async (userId: number, districtManagerNames: string[], action: 'add' | 'remove') => {
    const response = await api.post(`/api/admin/users/managers/${userId}/reassign-districts`, {
      district_manager_names: districtManagerNames,
      action,
    });
    return response.data;
  },

  listManagers: async (params?: { page?: number; per_page?: number; role?: 'regional' | 'district'; is_active?: boolean; search?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.page) queryParams.append('page', params.page.toString());
    if (params?.per_page) queryParams.append('per_page', params.per_page.toString());
    if (params?.role) queryParams.append('role', params.role);
    if (params?.is_active !== undefined) queryParams.append('is_active', params.is_active.toString());
    if (params?.search) queryParams.append('search', params.search);

    const response = await api.get(`/api/admin/users/managers?${queryParams.toString()}`);
    return response.data;
  },

  // Password Management
  resetPassword: async (userId: number) => {
    const response = await api.post(`/api/admin/users/${userId}/password/reset`);
    return response.data;
  },

  changePassword: async (userId: number, newPassword: string) => {
    const response = await api.post(`/api/admin/users/${userId}/password/change`, {
      new_password: newPassword,
    });
    return response.data;
  },

  // Status Management
  toggleUserStatus: async (userId: number) => {
    const response = await api.patch(`/api/admin/users/${userId}/status`);
    return response.data;
  },

  // Priority Level Management (Phase 2.2.2)
  updateDealerPriority: async (dealerId: string, priorityLevel: 'High' | 'Medium' | 'Low') => {
    const response = await api.put(`/api/admin/dealer/${dealerId}/priority`, {
      priority_level: priorityLevel,
    });
    return response.data;
  },

  bulkUpdatePriority: async (updates: Array<{ dealer_id: string; priority_level: string }>) => {
    const response = await api.post('/api/admin/dealers/bulk-priority', {
      updates,
    });
    return response.data;
  },

  // Alert Banner Management
  getActiveAlert: async () => {
    const response = await api.get('/api/admin/alerts/active');
    return response.data;
  },

  setAlert: async (message: string) => {
    const response = await api.put('/api/admin/alerts', { message });
    return response.data;
  },

  clearAlert: async () => {
    const response = await api.delete('/api/admin/alerts');
    return response.data;
  },

  // Phase 2.2.4: Advanced Coverage Analysis
  getAdvancedCoverageAnalysis: async (params?: {
    priority?: 'High' | 'Medium' | 'Low';
    channel?: 'Display' | 'Radio' | 'TV' | 'YouTube' | 'Search' | 'DOOH' | 'CTV/OTT';
    visibility_status?: 'zero' | 'critical' | 'low' | 'fair' | 'good';
    start_date?: string;
    end_date?: string;
    limit?: number;
    skip?: number;
  }) => {
    const queryParams = new URLSearchParams();
    if (params?.priority) queryParams.append('priority', params.priority);
    if (params?.channel) queryParams.append('channel', params.channel);
    if (params?.visibility_status) queryParams.append('visibility_status', params.visibility_status);
    if (params?.start_date) queryParams.append('start_date', params.start_date);
    if (params?.end_date) queryParams.append('end_date', params.end_date);
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.skip) queryParams.append('skip', params.skip.toString());

    const response = await api.get(`/api/admin/coverage-analysis-advanced?${queryParams.toString()}`);
    return response.data;
  },

  // Login Activity
  getRecentActivity: async (params?: { limit?: number; role?: string }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    if (params?.role) queryParams.append('role', params.role);
    const response = await api.get(`/api/admin/activity/recent?${queryParams.toString()}`);
    return response.data;
  },

  getActivitySummary: async () => {
    const response = await api.get('/api/admin/activity/summary');
    return response.data;
  },

  getUserLoginHistory: async (userId: number, params?: { limit?: number }) => {
    const queryParams = new URLSearchParams();
    if (params?.limit) queryParams.append('limit', params.limit.toString());
    const response = await api.get(`/api/admin/users/${userId}/login-history?${queryParams.toString()}`);
    return response.data;
  },
};

export default api;
